import React, { useMemo, useState } from 'react';
import Divider from '@monofront/design-system/components/Divider';
import PlanGroup from './PlanGroup';
import PlanCard from './PlanCard';
import Period from './PlanCard/components/Period';
import PricePerPeriod from './PlanCard/components/PricePerPeriod';
import PaymentPlan from './PlanCard/components/PaymentPlan';
import ArgumentsList from './PlanCard/components/ArgumentsList';
import ArgumentsListItem from './PlanCard/components/ArgumentsListItem';
import SelectButton from './PlanCard/components/SelectButton';
import { useTranslations } from 'next-intl';
import { useIntlFormatter } from '@hooks/useIntlFormatter';
import { formatDate, getEndOfFreeTrialDate } from '@monofront/business/utils';
import useSubscriptionPrice from '@hooks/useSubscriptionPrice';
import QuestionMarkOutline from '@monofront/design-system/visuals/Icons/QuestionMarkOutline';
import SavingsInfosSidePanel from './SavingsSidePanel';
import GuaranteesInfosSidePanel from './GuaranteesSidePanel';
import { useCheckoutCartState } from '@monofront/business/contexts';
import { MembershipPlan } from '@monofront/types';
import { useAccessibleClickGroup } from '@hooks/a11y/useAccessibleClickGroup';

type MembershipPlanPickerProps = {
  savings: number;
};

const MembershipPlanPicker: React.FC<MembershipPlanPickerProps> = ({
  savings,
}) => {
  const { mainLinkRef: yearlyLinkRef, onGroupClick: yearlyOnGroupClick } =
    useAccessibleClickGroup();
  const { mainLinkRef: monthlyLinkRef, onGroupClick: monthlyOnGroupClick } =
    useAccessibleClickGroup();
  const intlFormatter = useIntlFormatter();
  const tCheckout = useTranslations('Checkout');
  const { annualSubscriptionPrice, monthlySubscriptionPrice } =
    useSubscriptionPrice();

  const [isSavingsInfosSidePanelOpen, setIsSavingsInfosSidePanelOpen] =
    useState(false);
  const [isGuaranteesInfosSidePanelOpen, setIsGuaranteesInfosSidePanelOpen] =
    useState(false);
  const { membershipPlan, setMembershipPlan, checkoutCart } =
    useCheckoutCartState();

  const totalSavings = useMemo(
    () =>
      (checkoutCart?.items.reduce(
        (total, item) =>
          total + (item.variantOriginalPrice - item.unitPrice) * item.quantity,
        0,
      ) ?? 0) / 100,
    [checkoutCart?.items],
  );

  return (
    <section className="tw-pb-3">
      <SavingsInfosSidePanel
        isOpen={isSavingsInfosSidePanelOpen}
        onClose={() => setIsSavingsInfosSidePanelOpen(false)}
        dataTestId={'Checkout savings informations'}
        subscriptionAmount={annualSubscriptionPrice}
        savingsAmount={totalSavings}
      />
      <GuaranteesInfosSidePanel
        isOpen={isGuaranteesInfosSidePanelOpen}
        onClose={() => setIsGuaranteesInfosSidePanelOpen(false)}
        dataTestId={'Checkout guarantee informations'}
      />
      <div className="tw-relative tw-mb-4 tw-flex tw-gap-2">
        <p className="tw-lf-body-bold-md">
          {tCheckout('membershipPlanPickerTitle')}
        </p>
        <button
          type="button"
          className="tw-lf-link-icon tw-size-6 tw-p-0"
          onClick={() => setIsGuaranteesInfosSidePanelOpen(true)}
        >
          <QuestionMarkOutline />
        </button>
      </div>
      <p className="tw-lf-body-regular-md tw-mb-8">
        {tCheckout.rich('membershipPlanPickerSavings', {
          savings: intlFormatter.formatCurrency(savings),
          span: (children) => (
            <span className="tw-lf-body-bold-md tw-text-blue-600">
              {children}
            </span>
          ),
        })}
      </p>
      <PlanGroup>
        <PlanCard
          dataTestId="Checkout monthly membership plan picker"
          isSelected={membershipPlan === MembershipPlan.MONTHLY}
          onClick={monthlyOnGroupClick}
        >
          <Period>{tCheckout('membershipPlanPickerPlanMonthly')}</Period>
          <PricePerPeriod>
            {intlFormatter.formatCurrency(monthlySubscriptionPrice)}
            <span className="tw-lf-title-xs desktop:tw-lf-title-sm">
              {tCheckout('membershipPlanPickerPlanMonthlyPeriod')}
            </span>
          </PricePerPeriod>
          <PaymentPlan>
            {tCheckout('membershipPlanPickerPlanMonthlyRenewPlan')}
          </PaymentPlan>
          <Divider marginVertical="desktop:tw-my-4 tw-my-3" />
          <ArgumentsList>
            <ArgumentsListItem UNSAFE_className="tw-line-through">
              {tCheckout('membershipPlanPickerPlanMonthlyArg1')}
            </ArgumentsListItem>
            <ArgumentsListItem UNSAFE_className="tw-line-through">
              {tCheckout('membershipPlanPickerPlanMonthlyArg2')}
            </ArgumentsListItem>
            <ArgumentsListItem>
              {tCheckout('membershipPlanPickerPlanMonthlyArg3')}
            </ArgumentsListItem>
          </ArgumentsList>
          <SelectButton
            ref={monthlyLinkRef}
            onClick={() => setMembershipPlan(MembershipPlan.MONTHLY)}
          >
            {membershipPlan === MembershipPlan.MONTHLY
              ? tCheckout('membershipPlanPickerSelected')
              : tCheckout('membershipPlanPickerSelect')}
          </SelectButton>
        </PlanCard>
        <PlanCard
          dataTestId="Checkout yearly membership plan picker"
          isSelected={membershipPlan === MembershipPlan.YEARLY}
          onClick={yearlyOnGroupClick}
          isHighlighted
        >
          <Period>{tCheckout('membershipPlanPickerPlanAnnual')}</Period>
          <PricePerPeriod>
            {tCheckout('membershipPlanPickerPlanAnnualPeriod')}
          </PricePerPeriod>
          <PaymentPlan>
            {tCheckout('membershipPlanPickerPlanAnnualRenewPlan', {
              monthlyPrice: intlFormatter.formatCurrency(
                annualSubscriptionPrice / 12,
              ),
              price: intlFormatter.formatCurrency(annualSubscriptionPrice),
            })}
          </PaymentPlan>
          <Divider marginVertical="desktop:tw-my-4 tw-my-3" />
          <ArgumentsList>
            <ArgumentsListItem>
              {tCheckout('membershipPlanPickerPlanAnnualArg1', {
                savingsPercents: Math.floor(
                  (savings / annualSubscriptionPrice) * 100,
                ),
              })}
              <button
                type="button"
                className="tw-lf-link-icon tw-ml-2 tw-inline-flex tw-size-4 tw-p-0 tw-align-baseline"
                onClick={() => setIsSavingsInfosSidePanelOpen(true)}
              >
                <QuestionMarkOutline />
              </button>
            </ArgumentsListItem>
            <ArgumentsListItem>
              {tCheckout('membershipPlanPickerPlanAnnualArg2', {
                trialEndDate: formatDate(getEndOfFreeTrialDate()),
              })}
            </ArgumentsListItem>
          </ArgumentsList>
          <SelectButton
            ref={yearlyLinkRef}
            onClick={() => setMembershipPlan(MembershipPlan.YEARLY)}
          >
            {membershipPlan === MembershipPlan.YEARLY
              ? tCheckout('membershipPlanPickerSelected')
              : tCheckout('membershipPlanPickerSelect')}
          </SelectButton>
        </PlanCard>
      </PlanGroup>
    </section>
  );
};

export default MembershipPlanPicker;
